import { useState } from "react";
import { Search, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { usePartners, type PartnerListItem } from "@/hooks/use-wholesale";
import { useDebounce } from "@/hooks/use-debounce";
import { fmtPct } from "@/lib/wholesale-format";

/** Prvi ekran prezentacije. Partner se bira prije nego se išta pokaže kupcu jer
 *  njegov rabat određuje svaku cijenu koja će se pojaviti na ekranu. */
export function ShowroomPartnerPicker({ onPick }: { onPick: (partner: PartnerListItem) => void }) {
  const [q, setQ] = useState("");
  const debounced = useDebounce(q);
  const { data, isLoading } = usePartners(debounced);
  const partners = data ?? [];

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-5 p-5 pt-16">
      <div className="flex flex-col gap-1.5">
        <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Prezentacija</span>
        <h1 className="text-2xl font-semibold leading-tight">Za koga pokazuješ katalog?</h1>
        <p className="text-xs text-muted-foreground">
          Cijene u izlogu se odmah računaju s rabatom odabranog partnera.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="h-12 pl-10 text-base"
          placeholder="Pretraži partnere…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-px bg-foreground/10">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : partners.length === 0 ? (
        <p className="py-10 text-center text-xs text-muted-foreground">
          {debounced.trim() ? `Nema partnera za „${debounced.trim()}".` : "Još nema partnera. Dodaj ih u Partneri."}
        </p>
      ) : (
        <div className="flex flex-col gap-px bg-foreground/10 ring-1 ring-foreground/10">
          {partners.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onPick(p)}
              className="flex items-center gap-3 bg-background px-3 py-3 text-left transition-colors active:bg-muted"
            >
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-sm font-medium">{p.name}</span>
                <span className="truncate text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                  {[p.city, p.contactName].filter(Boolean).join(" · ") || "bez adrese"}
                </span>
              </span>
              {p.discountPct > 0 && (
                <span className="shrink-0 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.08em] tabular-nums ring-1 ring-foreground/20">
                  rabat {fmtPct(p.discountPct)}
                </span>
              )}
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
